import {
    AlertTriangle,
    Download,
    FileText,
    FileX,
    Hourglass,
    LoaderCircle,
    RotateCcw,
} from "lucide-react";
import { useQueries, useQuery } from "@tanstack/react-query";
import {
    type FC,
    type ReactNode,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { toast } from "sonner";

import {
    documentInfoQueryOptions,
    type IReportVersionSummary,
    readReportsEnabled,
    ReportRetryError,
} from "@/api";
import { isOutputDocumentMetadataNotFoundError } from "@/api/documents";
import { isAxiosNotFoundError } from "@/api/errors";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import {
    type ReportRetryOutcome,
    useConversationReports,
    useIsReportRetrying,
    useRetryReport,
} from "@/hooks/useConversationReports";
import { useOutputDocumentDownload } from "@/hooks/useOutputDocumentDownload";
import { cn } from "@/lib/utils";
import type { DocumentViewerOpenContext } from "@/pages/frontend/chat/contexts/DocumentViewerContext.types";
import { useDocumentViewer } from "@/pages/frontend/chat/contexts/useDocumentViewer";

export type ReportCardStatus =
    | "waiting"
    | "pending"
    | "processing"
    | "completed"
    | "failed";

export interface ReportWaitingDocument {
    id: number;
    name: string | null;
    isLoading: boolean;
    isMissing: boolean;
}

const TOUCH_TARGET = "max-sm:h-11 max-sm:px-3.5";

function waitingTitle(count: number): string {
    if (count === 1) return "Waiting for 1 document to finish processing";
    if (count > 1) return `Waiting for ${count} documents to finish processing`;
    return "Waiting for documents to finish processing";
}

function waitingDetail(count: number): string {
    return count === 1
        ? "You can leave this chat; the report will be prepared when it's ready."
        : "You can leave this chat; the report will be prepared when they're ready.";
}

const Title: FC<{ children: ReactNode; className?: string }> = ({
    children,
    className,
}) => (
    <p
        data-testid="report-card-title"
        className={cn(
            "text-foreground text-sm leading-tight font-semibold",
            className
        )}
    >
        {children}
    </p>
);

const Detail: FC<{ children: string }> = ({ children }) => (
    <p
        data-testid="report-card-detail"
        className="text-muted-foreground mt-1 text-[0.8125rem] leading-snug"
    >
        {children}
    </p>
);

const IconTile: FC<{ children: ReactNode; className?: string }> = ({
    children,
    className,
}) => (
    <span
        className={cn(
            "flex size-[2.375rem] shrink-0 items-center justify-center rounded-[0.625rem]",
            className
        )}
        aria-hidden
    >
        {children}
    </span>
);

export interface ReportCardViewProps {
    status: ReportCardStatus;
    versionNumber: number;
    waitingDocuments: readonly ReportWaitingDocument[];
    documentName: string | null;
    isDocumentNameLoading: boolean;
    artifactAvailable: boolean;
    retryEnabled: boolean;
    isRetrying: boolean;
    retryError: string | null;
    isDownloading: boolean;
    onOpen: () => void;
    onDownload: () => void;
    onRetry: () => void;
}

export const ReportCardView: FC<ReportCardViewProps> = ({
    status,
    versionNumber,
    waitingDocuments,
    documentName,
    isDocumentNameLoading,
    artifactAvailable,
    retryEnabled,
    isRetrying,
    retryError,
    isDownloading,
    onOpen,
    onDownload,
    onRetry,
}) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const lastFocusedRef = useRef<Element | null>(null);

    useEffect(() => {
        const last = lastFocusedRef.current;
        const active = document.activeElement;
        if (
            last !== null &&
            !last.isConnected &&
            (active === null || active === document.body)
        ) {
            rootRef.current?.focus();
        }
    });

    const preparing = status === "pending" || status === "processing";
    const completed = status === "completed";
    const available = completed && artifactAvailable;
    const removed = completed && !artifactAvailable;
    const waitingCount = waitingDocuments.length;

    let icon: ReactNode;
    let iconClass: string;
    let cardClass: string;
    let title: ReactNode;
    let detail: string | null = null;
    if (status === "waiting") {
        icon = <Hourglass className="size-[1.125rem]" />;
        iconClass = "bg-muted text-muted-foreground";
        cardClass = "";
        title = waitingTitle(waitingCount);
        detail = waitingDetail(waitingCount);
    } else if (preparing) {
        icon = (
            <LoaderCircle className="size-[1.125rem] animate-spin motion-reduce:animate-none" />
        );
        iconClass = "bg-primary/10 text-primary";
        cardClass =
            "border-primary/20 bg-primary/[0.04] dark:border-primary/25 dark:bg-primary/[0.08]";
        title = "Preparing report";
    } else if (removed) {
        icon = <FileX className="size-[1.125rem]" />;
        iconClass = "border-border bg-card text-muted-foreground border";
        cardClass = "bg-muted";
        title = "This report was removed";
        detail = "Its PDF is no longer available to open or download.";
    } else if (completed) {
        icon = <FileText className="size-[1.1875rem]" />;
        iconClass = "bg-destructive/10 text-destructive";
        cardClass =
            "border-primary/20 bg-primary/[0.04] dark:border-primary/25 dark:bg-primary/[0.08]";
        title = isDocumentNameLoading ? (
            <Skeleton
                data-testid="report-card-name-loading"
                className="h-[1.125rem] w-4/5 max-w-72"
            />
        ) : (
            <span className="block truncate" title={documentName ?? undefined}>
                {documentName ?? "Report"}
            </span>
        );
    } else {
        icon = <AlertTriangle className="size-[1.1875rem]" />;
        iconClass = "bg-destructive/10 text-destructive";
        cardClass = "border-destructive/25";
        title = "We couldn't prepare this report";
    }

    return (
        <Card
            ref={rootRef}
            tabIndex={-1}
            data-testid="report-card"
            data-status={status}
            role="group"
            aria-label="Report"
            aria-busy={preparing ? true : undefined}
            onFocus={(event) => {
                lastFocusedRef.current = event.target;
            }}
            className={cn(
                "focus-visible:ring-ring/50 mt-3 flex-row items-start gap-3 border p-3.5 ring-0 outline-none focus-visible:ring-3",
                cardClass
            )}
        >
            <IconTile className={iconClass}>{icon}</IconTile>
            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <div
                        data-testid="report-card-status"
                        role="status"
                        aria-live="polite"
                        aria-atomic="true"
                        className="min-w-0 flex-1 basis-40"
                    >
                        <Title
                            className={cn(removed && "text-muted-foreground")}
                        >
                            {title}
                        </Title>
                        {detail && <Detail>{detail}</Detail>}
                        {completed && (
                            <p
                                data-testid="report-card-meta"
                                className="text-muted-foreground mt-1 text-xs leading-tight"
                            >
                                <span>PDF</span>
                                <span aria-hidden> · </span>
                                <span>{`Version ${versionNumber}`}</span>
                            </p>
                        )}
                    </div>
                    {available && (
                        <div className="flex shrink-0 flex-wrap items-center gap-2">
                            <Button
                                type="button"
                                size="sm"
                                aria-label="Open report"
                                onClick={onOpen}
                                className={TOUCH_TARGET}
                            >
                                Open
                            </Button>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                aria-label="Download report"
                                onClick={onDownload}
                                disabled={isDownloading}
                                focusableWhenDisabled
                                aria-busy={isDownloading || undefined}
                                className={TOUCH_TARGET}
                            >
                                {isDownloading ? (
                                    <LoaderCircle
                                        data-icon="inline-start"
                                        className="animate-spin motion-reduce:animate-none"
                                    />
                                ) : (
                                    <Download data-icon="inline-start" />
                                )}
                                Download
                            </Button>
                        </div>
                    )}
                </div>

                {status === "waiting" && waitingCount > 0 && (
                    <ul
                        aria-label="Documents still processing"
                        className="mt-2 flex flex-col gap-1"
                    >
                        {waitingDocuments.map((document) => (
                            <li
                                key={document.id}
                                data-testid="report-waiting-document"
                                className="text-foreground/80 min-w-0 text-xs leading-snug"
                            >
                                {document.isLoading ? (
                                    <Skeleton className="h-3.5 w-3/5 max-w-60" />
                                ) : (
                                    <span
                                        className="block truncate"
                                        title={document.name ?? undefined}
                                    >
                                        {document.name ??
                                            (document.isMissing
                                                ? "Document no longer available"
                                                : "Document name unavailable")}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}

                {status === "failed" && retryEnabled && (
                    <Button
                        type="button"
                        onClick={onRetry}
                        disabled={isRetrying}
                        focusableWhenDisabled
                        aria-busy={isRetrying || undefined}
                        className={cn("mt-3", TOUCH_TARGET)}
                    >
                        {isRetrying ? (
                            <LoaderCircle
                                data-icon="inline-start"
                                className="animate-spin motion-reduce:animate-none"
                            />
                        ) : (
                            <RotateCcw data-icon="inline-start" />
                        )}
                        Try again
                    </Button>
                )}
                {status === "failed" && retryError && (
                    <p
                        role="alert"
                        data-testid="report-card-retry-error"
                        className="text-destructive mt-2 text-[0.8125rem] leading-snug"
                    >
                        {retryError}
                    </p>
                )}
            </div>
        </Card>
    );
};

const RETRY_REFUSAL_COPY: Readonly<Record<string, string>> = {
    reports_not_enabled: "Reports are turned off for your organization.",
    report_in_progress:
        "Another version of this report is already being prepared.",
    report_not_retryable: "This report can't be tried again right now.",
    retry_limit_reached:
        "This report can't be tried again. Ask Polly to start a new report.",
};

const REPORT_GONE = "This report is no longer in this chat.";

function retryErrorCopy(error: unknown): string {
    if (error instanceof ReportRetryError) {
        return (
            RETRY_REFUSAL_COPY[error.code] ??
            "These documents can't make this report anymore. Ask Polly to start a new report."
        );
    }
    return "We couldn't start this report again. Try again.";
}

function retryOutcomeCopy(outcome: ReportRetryOutcome): string | null {
    switch (outcome.kind) {
        case "confirmed":
            return null;
        case "divergent":
            return "The report didn't restart. Try again.";
        case "unconfirmed":
            return "The report may have restarted, but this card couldn't refresh. Reload this chat to see its status.";
    }
}

const ReportCardBody: FC<{
    conversationId: number;
    report: IReportVersionSummary;
}> = ({ conversationId, report }) => {
    const { settings } = useOrganization();
    const { openDocumentById } = useDocumentViewer();
    const download = useOutputDocumentDownload();
    const retry = useRetryReport(conversationId, report.id);
    const isRetrying = useIsReportRetrying(conversationId, report.id);
    // A Try again message belongs to the row write it answered; any later write of the row retires it.
    const [retryError, setRetryError] = useState<{
        message: string;
        updatedAt: string;
    } | null>(null);
    const [removedDocumentId, setRemovedDocumentId] = useState<number | null>(
        null
    );
    const retryInFlightRef = useRef(false);
    const sawLiveRef = useRef(false);

    const { status } = report;
    const openableDocumentId =
        status === "completed" && report.artifact_available
            ? report.document_id
            : null;
    const documentQuery = useQuery({
        ...documentInfoQueryOptions(openableDocumentId!),
        enabled: openableDocumentId !== null,
        refetchOnWindowFocus: false,
    });

    const waitingIds =
        status === "waiting" ? (report.waiting_document_ids ?? []) : [];
    const waitingQueries = useQueries({
        queries: waitingIds.map((id) => ({
            ...documentInfoQueryOptions(id),
            refetchOnWindowFocus: false,
        })),
    });

    const openReport = useCallback(
        async (documentId: number, signal?: AbortSignal) => {
            const context: DocumentViewerOpenContext = {
                origin: "report-card",
                variant: "report_internal",
                fallbackToDefault: true,
                ...(signal ? { signal } : {}),
            };
            const outcome = await openDocumentById(
                documentId,
                undefined,
                context
            );
            if (outcome.type === "not-found") {
                setRemovedDocumentId(documentId);
            }
        },
        [openDocumentById]
    );

    useEffect(() => {
        if (
            status === "waiting" ||
            status === "pending" ||
            status === "processing"
        ) {
            sawLiveRef.current = true;
            return;
        }
        if (openableDocumentId === null || !sawLiveRef.current) return;
        const controller = new AbortController();
        void openReport(openableDocumentId, controller.signal);
        return () => controller.abort();
    }, [openReport, openableDocumentId, status]);

    const removed =
        report.document_id === null ||
        removedDocumentId === report.document_id ||
        isAxiosNotFoundError(documentQuery.error);

    const handleOpen = () => {
        if (report.document_id !== null) void openReport(report.document_id);
    };

    const handleDownload = () => {
        if (report.document_id === null) return;
        download.mutate(report.document_id, {
            onError: (error) => {
                if (isOutputDocumentMetadataNotFoundError(error)) {
                    setRemovedDocumentId(error.documentId);
                }
            },
        });
    };

    const handleRetry = async () => {
        if (retryInFlightRef.current) return;
        retryInFlightRef.current = true;
        setRetryError(null);
        const attemptedAt = report.updated_at;
        try {
            const outcome = await retry.mutateAsync();
            if (outcome.kind === "divergent" && outcome.version === null) {
                toast.error(REPORT_GONE);
                return;
            }
            const message = retryOutcomeCopy(outcome);
            if (message !== null) {
                setRetryError({
                    message,
                    updatedAt:
                        outcome.kind === "divergent" && outcome.version
                            ? outcome.version.updated_at
                            : attemptedAt,
                });
            }
        } catch (error) {
            if (isAxiosNotFoundError(error)) {
                toast.error(REPORT_GONE);
            } else {
                setRetryError({
                    message: retryErrorCopy(error),
                    updatedAt: attemptedAt,
                });
            }
        } finally {
            retryInFlightRef.current = false;
        }
    };

    return (
        <ReportCardView
            status={status}
            versionNumber={report.version_number}
            waitingDocuments={waitingIds.map((id, index) => ({
                id,
                name: waitingQueries[index].data?.document_name ?? null,
                isLoading: waitingQueries[index].isPending,
                isMissing: isAxiosNotFoundError(waitingQueries[index].error),
            }))}
            documentName={documentQuery.data?.document_name ?? null}
            isDocumentNameLoading={
                openableDocumentId !== null && documentQuery.isPending
            }
            artifactAvailable={report.artifact_available && !removed}
            retryEnabled={readReportsEnabled(settings)}
            isRetrying={isRetrying}
            retryError={
                retryError?.updatedAt === report.updated_at
                    ? retryError.message
                    : null
            }
            isDownloading={download.isPending}
            onOpen={handleOpen}
            onDownload={handleDownload}
            onRetry={() => void handleRetry()}
        />
    );
};

export interface ReportCardProps {
    conversationId: number;
    chatMessageId: number;
}

export const ReportCard: FC<ReportCardProps> = ({
    conversationId,
    chatMessageId,
}) => {
    const { data: reports } = useConversationReports(conversationId);
    const report = reports?.find(
        (row) => row.chat_message_id === chatMessageId
    );
    if (!report) return null;
    return (
        <ReportCardBody
            key={`${conversationId}:${report.id}`}
            conversationId={conversationId}
            report={report}
        />
    );
};
